import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    ShieldCheckIcon,
    ScaleIcon,
    LockClosedIcon,
    ArrowPathIcon,
} from '@heroicons/react/24/outline';

const AdminAppraisalManager = () => {
    const [activeTab, setActiveTab] = useState('approvals');

    const [pendingFinalization, setPendingFinalization] = useState([
        { id: 101, name: "Ravi Kumar Sharma", dept: "Engineering", managerRating: 4.5, selfRating: 4.2, recommendedHike: 12 },
        { id: 102, name: "Ananya Iyer", dept: "Design", managerRating: 4.8, selfRating: 4.7, recommendedHike: 15 },
        { id: 103, name: "Siddharth Malhotra", dept: "Marketing", managerRating: 3.2, selfRating: 3.8, recommendedHike: 5 },
    ]);

    const handleFinalApproval = (id) => {
        setPendingFinalization(prev => prev.filter(emp => emp.id !== id));
    };

    return (
        <div className="min-h-screen bg-white p-10 font-roboto text-slate-800">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@600;700&family=Roboto:wght@400;500;700&display=swap');
                .font-heading { font-family: 'Poppins', sans-serif; }
            `}</style>

            <div className="max-w-5xl mx-auto">
                {/* HEADER SECTION */}
                <header className="flex justify-between items-center mb-12 pb-8 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        <ShieldCheckIcon className="h-6 w-6 text-slate-900 stroke-[2]" />
                        <h1 className="text-xl font-bold font-heading text-slate-900 tracking-tight">Appraisal Management</h1>
                    </div>

                    <div className="flex gap-1 bg-slate-50 p-1 rounded border border-slate-200">
                        <button 
                            onClick={() => setActiveTab('setup')}
                            className={`px-5 py-2 rounded text-[11px] font-bold uppercase tracking-wider transition-all ${activeTab === 'setup' ? 'bg-white text-slate-900 shadow-sm border border-slate-200' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            Cycle Setup
                        </button>
                        <button 
                            onClick={() => setActiveTab('approvals')}
                            className={`px-5 py-2 rounded text-[11px] font-bold uppercase tracking-wider transition-all ${activeTab === 'approvals' ? 'bg-white text-slate-900 shadow-sm border border-slate-200' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            Final Calibration
                        </button>
                    </div>
                </header>

                {activeTab === 'setup' ? (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 lg:grid-cols-3 gap-16">
                        <div className="lg:col-span-2 space-y-10">
                            <h2 className="text-xs font-bold font-heading text-slate-500 uppercase tracking-[0.15em]">Initiate New Appraisal Cycle</h2>
                            
                            <div className="space-y-6">
                                <div className="max-w-md">
                                    <label className="text-[11px] font-bold text-slate-900 uppercase mb-2 block tracking-wide">Cycle Name</label>
                                    <input type="text" placeholder="Annual Performance Review 2026" className="w-full p-3 bg-white border border-slate-300 rounded font-medium text-sm outline-none focus:border-slate-900 transition-all placeholder:text-slate-300" />
                                </div>
                                <div className="grid grid-cols-2 gap-6 max-w-md">
                                    <div>
                                        <label className="text-[11px] font-bold text-slate-900 uppercase mb-2 block tracking-wide">Rating Scale</label>
                                        <select className="w-full p-3 bg-white border border-slate-300 rounded font-medium text-sm outline-none cursor-pointer">
                                            <option>1.0 - 5.0 Scale</option>
                                            <option>1.0 - 10.0 Scale</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-bold text-slate-900 uppercase mb-2 block tracking-wide">Release Date</label>
                                        <input type="date" className="w-full p-3 bg-white border border-slate-300 rounded font-medium text-sm outline-none" />
                                    </div>
                                </div>
                                <div className="pt-6">
                                    <button className="bg-slate-900 text-white font-bold py-3 px-8 rounded text-[11px] uppercase tracking-[0.2em] hover:bg-slate-800 transition-all active:scale-95">
                                        Launch Cycle
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-8 border-l border-slate-100 pl-10">
                            <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                <LockClosedIcon className="h-4 w-4" /> Policy Constraints
                            </h3>
                            <div className="space-y-5">
                                {["Auto-lock submissions", "Proof required for 4.5+", "Display Hike to Employee", "Peer Review Access"].map((rule, i) => (
                                    <div key={i} className="flex justify-between items-center pb-3 border-b border-slate-50">
                                        <span className="text-xs font-bold text-slate-700">{rule}</span>
                                        <input type="checkbox" defaultChecked className="accent-slate-900 h-4 w-4 cursor-pointer" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <ScaleIcon className="h-5 w-5 text-slate-400" />
                                <h2 className="text-xs font-bold font-heading text-slate-500 uppercase tracking-[0.15em]">Pending Calibration Queue</h2>
                            </div>
                        </div>

                        <div className="border border-slate-200 rounded overflow-hidden">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50/50">
                                        <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Employee Info</th>
                                        <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-center">Final Scores</th>
                                        <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Increment %</th>
                                        <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {pendingFinalization.map((emp) => (
                                        <tr key={emp.id} className="hover:bg-slate-50/50 transition-all">
                                            <td className="px-6 py-5">
                                                <div className="text-sm font-bold text-slate-900">{emp.name}</div>
                                                <div className="text-[10px] text-slate-400 font-bold uppercase tracking-tight mt-0.5">{emp.dept}</div>
                                            </td>
                                            <td className="px-6 py-5 text-center">
                                                <div className="inline-flex items-center gap-4">
                                                    <div>
                                                        <div className="text-[9px] font-bold text-slate-400 uppercase">Self</div>
                                                        <div className="text-xs font-bold text-slate-600">{emp.selfRating}</div>
                                                    </div>
                                                    <div className="h-6 w-[1px] bg-slate-200"></div>
                                                    <div>
                                                        <div className="text-[9px] font-bold text-slate-400 uppercase">Mgr</div>
                                                        <div className="text-xs font-black text-slate-900">{emp.managerRating}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-5">
                                                <div className="relative inline-block">
                                                    <input 
                                                        type="number" 
                                                        defaultValue={emp.recommendedHike} 
                                                        className="w-20 p-2 border border-slate-300 rounded font-bold text-sm text-slate-900 outline-none focus:border-slate-900 transition-all" 
                                                    />
                                                    <span className="absolute right-2 top-2.5 text-[10px] font-bold text-slate-400">%</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-5 text-right">
                                                <div className="flex justify-end gap-4">
                                                    <button className="text-slate-300 hover:text-slate-900 transition-all" title="Return to Manager">
                                                        <ArrowPathIcon className="h-5 w-5 stroke-[2]" />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleFinalApproval(emp.id)} 
                                                        className="bg-slate-900 text-white px-5 py-2 rounded text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all"
                                                    >
                                                        Finalize
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {pendingFinalization.length > 0 && (
                            <div className="pt-12 border-t border-slate-100 flex justify-between items-center">
                                <div className="text-[11px] font-medium text-slate-400 italic">
                                    * Finalizing will prepare the official appraisal letter.
                                </div>
                                <button className="bg-emerald-700 hover:bg-emerald-800 text-white px-8 py-3 rounded text-[11px] font-black uppercase tracking-[0.25em] transition-all shadow-md active:scale-95">
                                    Release Results to Employees
                                </button>
                            </div>
                        )}
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default AdminAppraisalManager;